/**
 * React Query keys for data whose answer depends on WHO is asking — RLS gives
 * a different row set to a different caller — shared by more than one file.
 *
 * The cache is cleared when the signed-in user changes (switchUserScope), but
 * a screen that stays mounted through a silent Privy account switch renders
 * whatever its key finds before that. A disabled query still returns cached
 * data, so `enabled: !!userId` is no defence: only the key is.
 *
 * The user id goes LAST, so prefix invalidations written before it existed
 * (`["workspace-members", questId]`, `["quests", questId]`) still match.
 *
 * A pure module on purpose: many tests mock hook modules wholesale, which
 * would turn a key factory exported from a hook into `undefined`.
 */

type Id = string | null | undefined;

/**
 * The worker's own negotiations (useMyNegotiations) — feeds My Work's
 * Interested/Negotiating lists and the /chats pending-application rows.
 */
export const myNegotiationsKey = (userId: Id) =>
  ["bounties", "negotiating", userId] as const;

/** Members of a quest's workspace — RLS (`is_workspace_member`) returns nothing to an outsider. */
export const workspaceMembersKey = (questId: Id, userId: Id) =>
  ["workspace-members", questId, userId] as const;

/** Applicants to a quest, from `swipe_history` — the creator sees all, a worker their own. */
export const workspaceApplicantsKey = (questId: Id, userId: Id) =>
  ["workspace-applicants", questId, userId] as const;

/** A quest's sourced candidates — third-party PII, creator-only under RLS. */
export const questCandidatesKey = (questId: Id, userId: Id) =>
  ["quests", questId, "candidates", userId] as const;

/**
 * A profile row as `viewerId` sees it. profiles RLS is owner-or-admin, so the
 * same id is a full row (email included) to its owner or an admin and nothing
 * to anyone else.
 */
export const profileKey = (profileId: Id, viewerId: Id) =>
  ["profiles", profileId, viewerId] as const;

/** A bounty's detail — embeds `submissions`, which RLS limits to the quest's parties. */
export const bountyDetailKey = (bountyId: Id, userId: Id) =>
  ["bounties", bountyId, userId] as const;
